// ============================================================================
// Recurring Checklists — book-model service surface.
// ----------------------------------------------------------------------------
// Post-Phase-3 (2026-08-10). Book model is authoritative: definitions live at
// `checklists/{id}`, per-occurrence state lives at
// `checklist_books/{ws}_{type}/pages/{pageKey}.completions[id]`, canvas
// layout at `checklist_books/{ws}_{type}/layout_versions/{versionId}`.
//
// This file used to carry the singleton-model implementation of every
// recurring operation (occurrence maps on `checklists/{id}`, daily_pages /
// weekly_pages collections, per-boot mirror settle / materialize / prune).
// All of that is deleted here. What remains:
//
//   - `bootstrapRecurringForWorkspace` — settings-doc ensure only.
//   - `setOccurrenceCompletion` — routes to the correct book page via
//     `setChecklistCompletionOnPage`.
//   - Type-swap wizard: `swapChecklistType`, `deriveChecklistTypeCopy`,
//     `itemHasConnections` (still used by ChecklistTypeSelector).
//   - Settings CRUD + subscriptions.
//   - Pure date / occurrence-key helpers used across the UI.
//
// See src/types/checklistBooks.ts for the physical model.
// ============================================================================
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { emitCanonicalChecklistActivity } from './checklistMutationService'
import { bootstrapChecklistsOnBoard } from './checklistWriter'
import {
  DEFAULT_RECURRING_WINDOW_DAYS,
  normalizeRecurringWindowDays,
  type ChecklistType,
  type RecurringMode,
  type WorkspaceRecurringSettings,
} from '../types/recurringChecklist'
export { DEFAULT_RECURRING_WINDOW_DAYS, normalizeRecurringWindowDays }
import type { ChecklistItem, ChecklistWorkflowStatus, RecurringOccurrenceState } from '../types/schema'
import { checklistPath } from '../types/checklistBooks'
import {
  pageKeyFor,
} from './checklistBookService'
import { projectLegacyChecklistTypeToNew } from './checklistViewAdapter'
import { primeTodosLeadDays } from './workspaceTodosLeadDaysCache'
import {
  formatZonedDateKey,
  getWeekBucketKey,
  resolveWorkspaceTimeZone,
  startOfIsoWeekDate,
} from './repeat/keys'

const SETTINGS = 'workspace_recurring_settings'
const ITEMS = 'checklists'

// ---------- Date helpers ---------------------------------------------------

export const isoDate = (date: Date, timeZone?: string): string => {
  const tz = timeZone ? resolveWorkspaceTimeZone(timeZone) : Intl.DateTimeFormat().resolvedOptions().timeZone
  return formatZonedDateKey(date, tz)
}

export const parseIsoDate = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date()
  date.setFullYear(y, m - 1, d)
  date.setHours(0, 0, 0, 0)
  return date
}

export const startOfIsoWeek = (date: Date, timeZone?: string): Date =>
  startOfIsoWeekDate(date, timeZone)

export const isoWeekKey = (date: Date, timeZone?: string): string => {
  const tz = timeZone ? resolveWorkspaceTimeZone(timeZone) : Intl.DateTimeFormat().resolvedOptions().timeZone
  return getWeekBucketKey(date, tz)
}

export const occurrenceKeyFor = (mode: RecurringMode, date: Date, timeZone?: string): string =>
  mode === 'DAILY' ? isoDate(date, timeZone) : isoWeekKey(date, timeZone)

/** Display title for an individual occurrence of a recurring checklist. In
 *  audit / notification / calendar / ledger contexts where a single dated
 *  instance is being surfaced (as opposed to the recurring master itself),
 *  render as `<title>_<key>` so viewers can tell which occurrence a given
 *  event refers to. MAIN items (or unknown key) pass through unchanged. */
export const formatOccurrenceTitle = (
  item: Pick<ChecklistItem, 'title' | 'checklistType'>,
  occurrenceKey: string | null | undefined,
): string => {
  const title = item.title ?? 'Untitled'
  const type = item.checklistType ?? 'INDIVIDUAL'
  if (type === 'INDIVIDUAL' || !occurrenceKey) return title
  return `${title}_${occurrenceKey}`
}

export const pageDateIsoFor = (mode: RecurringMode, date: Date, timeZone?: string): string =>
  mode === 'DAILY' ? isoDate(date, timeZone) : isoDate(startOfIsoWeek(date, timeZone), timeZone)

export const advanceDate = (mode: RecurringMode, date: Date, steps: number): Date => {
  const clone = new Date(date)
  const unit = mode === 'DAILY' ? 1 : 7
  clone.setDate(clone.getDate() + steps * unit)
  clone.setHours(0, 0, 0, 0)
  return clone
}

/** The "current" occurrence key for an item given today's clock. Falls back to
 *  today's ISO date for INDIVIDUAL items so callers don't have to branch. */
export const currentOccurrenceKeyForItem = (
  item: ChecklistItem,
  now: Date = new Date(),
  timeZone?: string,
): string => {
  const type = item.checklistType ?? 'INDIVIDUAL'
  if (type === 'WEEKLY') return isoWeekKey(startOfIsoWeek(now, timeZone), timeZone)
  return isoDate(now, timeZone)
}

// ---------- Effective read helpers ----------------------------------------

/** Read the effective completion for a recurring item at a specific key.
 *  Reads directly off the projected `ChecklistItem.occurrences[key]` map that
 *  checklistViewAdapter builds from the book page's completions entry. */
export const getEffectiveCompletion = (
  item: ChecklistItem,
  occurrenceKey?: string,
): { isCompleted: boolean; workflowStatus: ChecklistWorkflowStatus | null } => {
  const type = item.checklistType ?? 'INDIVIDUAL'
  if (type === 'INDIVIDUAL') {
    return { isCompleted: Boolean(item.isCompleted), workflowStatus: item.workflowStatus ?? null }
  }
  const key = occurrenceKey ?? currentOccurrenceKeyForItem(item)
  const entry = item.occurrences?.[key]
  if (!entry) {
    return { isCompleted: false, workflowStatus: null }
  }
  return {
    isCompleted: Boolean(entry.isCompleted),
    workflowStatus: (entry.workflowStatus as ChecklistWorkflowStatus | null) ?? null,
  }
}

// ---------- Settings ------------------------------------------------------

const DEFAULT_RETENTION_DAYS = 30

export const ensureWorkspaceRecurringSettings = async (workspaceId: string, isSubscriber: boolean): Promise<void> => {
  if (!workspaceId) return
  const ref = doc(firestoreDb, SETTINGS, workspaceId)
  const snap = await getDoc(ref)
  if (snap.exists()) return
  const payload: Omit<WorkspaceRecurringSettings, 'id'> = {
    workspaceId,
    retentionMode: isSubscriber ? 'KEEP_FOREVER' : 'PRUNE_AFTER_N',
    retentionDays: DEFAULT_RETENTION_DAYS,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    recordVersion: 2,
  }
  await writeBatch(firestoreDb).set(ref, payload).commit()
}

export const subscribeWorkspaceRecurringSettings = (
  workspaceId: string,
  onData: (settings: WorkspaceRecurringSettings | null) => void,
  onError?: (error: unknown) => void,
) => {
  const ref = doc(firestoreDb, SETTINGS, workspaceId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        // Clear cached lead-days so projections don't retain a stale value
        // for a workspace whose settings doc has just been deleted.
        primeTodosLeadDays(workspaceId, null)
        onData(null); return
      }
      const settings = { id: snap.id, ...snap.data() } as WorkspaceRecurringSettings
      primeTodosLeadDays(workspaceId, settings.todosLeadDays ?? 0)
      onData(settings)
    },
    // Explicit error handler prevents Firestore from surfacing uncaught
    // "permission-denied" snapshot errors to the console (they were spamming
    // the app when the active workspace was a preview / demo where the user
    // has no membership doc). Callers get the raw error and decide what to do.
    (error) => {
      if (onError) { onError(error); return }
      onData(null)
    },
  )
}

export const DEFAULT_ARCHIVE_THRESHOLD_WINDOWS = 2

export const updateRecurringSettings = async (
  workspaceId: string,
  patch: Partial<Pick<WorkspaceRecurringSettings, 'retentionMode' | 'retentionDays' | 'recurringWindowDays' | 'archiveThresholdWindows'>>,
): Promise<void> => {
  const ref = doc(firestoreDb, SETTINGS, workspaceId)
  const clean: Record<string, unknown> = { ...patch, updatedAt: serverTimestamp() }
  if (clean.recurringWindowDays !== undefined) {
    clean.recurringWindowDays = DEFAULT_RECURRING_WINDOW_DAYS
  }
  if (clean.archiveThresholdWindows !== undefined) {
    const raw = Number(clean.archiveThresholdWindows) || DEFAULT_ARCHIVE_THRESHOLD_WINDOWS
    clean.archiveThresholdWindows = Math.min(5, Math.max(1, Math.round(raw)))
  }
  await updateDoc(ref, clean)
}

// ---------- Occurrence completion write ------------------------------------

export type OccurrenceCompletionPatch = {
  isCompleted: boolean
  workflowStatus?: ChecklistWorkflowStatus | null
  actorId?: string
  actorName?: string
}

/** Write completion for a single occurrence of a recurring checklist. Reads
 *  the definition to derive (workspaceId, checklistType), then writes the
 *  completion state to the corresponding book page's `completions[id]`
 *  entry via `setChecklistCompletionOnPage`. */
export const setOccurrenceCompletion = async (
  itemId: string,
  occurrenceKey: string,
  patch: OccurrenceCompletionPatch,
): Promise<void> => {
  const defSnap = await getDoc(doc(firestoreDb, checklistPath(itemId)))
  if (!defSnap.exists()) {
    throw new Error(`setOccurrenceCompletion: checklist definition ${itemId} not found`)
  }
  const def = defSnap.data() as {
    workspaceId?: string
    checklistType?: string
    title?: string
    board?: string
    recordVersion?: number
    workflowStatus?: ChecklistWorkflowStatus
  }
  if (!def.workspaceId) {
    throw new Error(`setOccurrenceCompletion: definition ${itemId} missing workspaceId`)
  }
  const type = projectLegacyChecklistTypeToNew(def.checklistType as ChecklistType | undefined)
  const bookId = `${def.workspaceId}_${type}`
  const isCompleted = patch.isCompleted
  const workflow = patch.workflowStatus ?? (isCompleted ? 'COMPLETED' : 'ONGOING')

  const { updateChecklistWorkflowStatus } = await import('./checklistStatusWriter')
  const { applyStripboardDelta } = await import('./stripboardDeltaService')

  await updateChecklistWorkflowStatus({
    item: {
      id: itemId,
      boardStatus: isCompleted ? 'COMPLETE' : 'TODO',
      recordVersion: typeof def.recordVersion === 'number' ? def.recordVersion : 1,
      workflowStatus: def.workflowStatus ?? null,
      workspaceId: def.workspaceId,
      checklistType: type,
    },
    workflowStatus: workflow,
    periodKey: occurrenceKey,
    runDelta: applyStripboardDelta,
    actor: patch.actorId ? { id: patch.actorId, name: patch.actorName ?? undefined } : undefined,
  })

  // Best-effort audit emit. The composite title (`<title>_<key>`) lets
  // reviewers distinguish which occurrence was ticked / reopened.
  const occurrenceTitle = def.title
    ? patch.isCompleted
      ? `Completed ${def.title}_${occurrenceKey}`
      : `Reopened ${def.title}_${occurrenceKey}`
    : `Checklist ${itemId} @ ${occurrenceKey}`
  await emitCanonicalChecklistActivity(firestoreDb, {
    workspaceId: def.workspaceId,
    actor: { id: patch.actorId ?? null, name: patch.actorName ?? null },
    targetId: itemId,
    targetTitle: def.title ?? itemId,
    action: patch.isCompleted ? 'CHECKLIST_COMPLETED' : 'CHECKLIST_REOPENED',
    summary: occurrenceTitle,
    board: (def.board as ChecklistItem['board']) ?? 'DASHBOARD',
    metadata: { occurrenceKey, bookId, pageKey: occurrenceKey },
  }).catch((error) => console.warn('[recurringChecklist] audit emit failed', error))
}

// ---------- Type swap ------------------------------------------------------

export type SwapTypeInput = {
  workspaceId: string
  itemId: string
  fromType: ChecklistType
  toType: ChecklistType
  actorId?: string
  actorName?: string
}

/** Swap a checklist's `checklistType` in place — used only for the
 *  DAILY ↔ WEEKLY case (same family). Cross-family swaps (INDIVIDUAL ↔
 *  recurring) go through `deriveChecklistTypeCopy` instead because the
 *  dependency graph and canvas position need clearing.
 *
 *  Idempotent no-op if `fromType === toType`. Uses the live snapshot for
 *  the source-of-truth `checklistType` so a stale `fromType` argument
 *  can't double-apply the wrong branch. */
export const swapChecklistType = async (input: SwapTypeInput): Promise<void> => {
  const itemRef = doc(firestoreDb, ITEMS, input.itemId)
  const itemSnap = await getDoc(itemRef)
  if (!itemSnap.exists()) throw new Error('Checklist item not found.')
  const item = itemSnap.data() as ChecklistItem
  const actualFrom: ChecklistType = item.checklistType ?? 'INDIVIDUAL'
  const target = input.toType
  if (actualFrom === target) return

  // Parent-scoped recurring: DAILY↔WEEKLY swaps stay under the same parent,
  // but the recurringWindow must be present — mirror it fresh from the
  // parent element in case the item was created before the refactor.
  const nextRecurringWindow = target === 'DAILY' || target === 'WEEKLY'
    ? await resolveRecurringWindowFromParent(item.parentContainerId, item.workspaceId)
    : null

  await updateDoc(itemRef, {
    checklistType: target,
    ...(nextRecurringWindow
      ? { recurringWindow: { ...nextRecurringWindow, syncedAt: serverTimestamp() } }
      : {}),
    updatedAt: serverTimestamp(),
    ...(input.actorId ? { lastUpdatedBy: input.actorId } : {}),
    ...(input.actorName ? { lastUpdatedByName: input.actorName } : {}),
  })
}

/** Fetch a parent element's start/end dates and return the shape we
 *  persist onto recurring definitions. Throws when the parent is missing
 *  or ineligible so callers can bail out with a real error message. */
const resolveRecurringWindowFromParent = async (
  parentContainerId: string | null | undefined,
  workspaceId: string | null | undefined,
): Promise<{ parentElementId: string; startDate: string; endDate: string }> => {
  if (!parentContainerId) {
    throw new Error('Recurring checklists must live inside a Task, Goal, or Subtask.')
  }
  if (parentContainerId.startsWith('recurring:')) {
    throw new Error('Top-level recurring canvases are retired — move this item under a Task, Goal, or Subtask first.')
  }
  const parentSnap = await getDoc(doc(firestoreDb, 'elements', parentContainerId))
  if (!parentSnap.exists()) {
    throw new Error('The parent element for this recurring checklist no longer exists.')
  }
  const parentData = parentSnap.data() as {
    workspaceId?: string
    type?: 'GOAL' | 'TASK' | 'SUBTASK'
    startDate?: string | null
    endDate?: string | null
  }
  if (workspaceId && parentData.workspaceId && parentData.workspaceId !== workspaceId) {
    throw new Error('The parent element belongs to a different workspace.')
  }
  if (parentData.type !== 'GOAL' && parentData.type !== 'TASK' && parentData.type !== 'SUBTASK') {
    throw new Error('Recurring checklists can only live inside a Task, Goal, or Subtask.')
  }
  if (typeof parentData.startDate !== 'string' || typeof parentData.endDate !== 'string') {
    throw new Error('Set a start date and end date on the parent before switching to recurring.')
  }
  const start = parentData.startDate.slice(0, 10)
  const end = parentData.endDate.slice(0, 10)
  if (end < start) {
    throw new Error("The parent's end date is before its start date.")
  }
  return { parentElementId: parentContainerId, startDate: start, endDate: end }
}

// ---------- Derive-copy (cross-family Individual ↔ Recurring) -------------

export type DeriveCopyInput = {
  workspaceId: string
  sourceItemId: string
  toType: ChecklistType
  parentContainerId: string
  createdById: string
  createdByName: string
}

export type DeriveCopyResult = { newItemId: string }

/** Cross-family type change: read the source and materialize a NEW
 *  `checklists/{id}` doc of the target type with:
 *    - a fresh id (Firestore auto)
 *    - empty prerequisites / dependencyNodeIds / blockedByNodeIds /
 *      isBlockerForNodeIds / gateInputIds  → dependency graph is cleared
 *    - position zeroed so the canvas can grid-append at the bottom
 *    - createdBy* set to the actor performing the derive
 *
 *  The source item is left untouched. Caller is responsible for showing
 *  the new item's detail popup (returned id) and, if desired, appending
 *  the matching canvas node projection. */
export const deriveChecklistTypeCopy = async (input: DeriveCopyInput): Promise<DeriveCopyResult> => {
  const sourceRef = doc(firestoreDb, ITEMS, input.sourceItemId)
  const sourceSnap = await getDoc(sourceRef)
  if (!sourceSnap.exists()) throw new Error('Source checklist item not found.')
  const source = sourceSnap.data() as ChecklistItem
  if (source.workspaceId !== input.workspaceId) {
    throw new Error('Source item belongs to a different workspace.')
  }

  // Parent-scoped recurring: when deriving INTO a recurring type, the new
  // definition needs a recurringWindow mirrored from the parent element.
  // Deriving BACK to INDIVIDUAL clears any recurringWindow so a stale
  // window from the source doesn't leak onto the copy.
  const derivedRecurringWindow = input.toType === 'DAILY' || input.toType === 'WEEKLY'
    ? await resolveRecurringWindowFromParent(input.parentContainerId, input.workspaceId)
    : null

  const newRef = doc(collection(firestoreDb, ITEMS))
  const nowIso = new Date().toISOString()

  const derived: Record<string, unknown> = {
    ...source,
    id: newRef.id,
    checklistType: input.toType,
    parentContainerId: input.parentContainerId,
    // Connections cleared on the derived copy — this is the wizard's contract.
    prerequisites: [],
    dependencyNodeIds: [],
    blockedByNodeIds: [],
    isBlockerForNodeIds: [],
    gateInputIds: [],
    isArchived: false,
    position: { x: 0, y: 0 },
    // Recurring window — populate for DAILY/WEEKLY, drop when derived to INDIVIDUAL.
    ...(derivedRecurringWindow
      ? { recurringWindow: { ...derivedRecurringWindow, syncedAt: serverTimestamp() } }
      : { recurringWindow: null }),
    // Provenance / audit
    sourceChecklistId: newRef.id,
    createdById: input.createdById,
    createdByName: input.createdByName,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastUpdatedBy: input.createdById,
    lastUpdatedByName: input.createdByName,
    derivedFromChecklistId: input.sourceItemId,
    derivedAtIso: nowIso,
    recordVersion: 1,
  }

  await setDoc(newRef, derived)
  // Bootstrap the derived checklist onto the destination book so the layout
  // picks a real empty slot. Prior behavior wrote `position: {x:0, y:0}`
  // and never registered with the layout — two derives on the same canvas
  // both landed at the origin and stacked. For DAILY/WEEKLY, propagates
  // across the recurringWindow via `spreadChecklistAcrossRecurringPages`
  // inside bootstrap. Best-effort — bootstrap never throws.
  await bootstrapChecklistsOnBoard(
    input.workspaceId,
    [{
      id: newRef.id,
      checklistType: input.toType,
      recurringWindow: derivedRecurringWindow
        ? { startDate: derivedRecurringWindow.startDate, endDate: derivedRecurringWindow.endDate }
        : undefined,
    }],
    { uid: input.createdById, displayName: input.createdByName ?? null },
  )
  return { newItemId: newRef.id }
}

// ---------- Connection guard ----------------------------------------------

export const itemHasConnections = (item: ChecklistItem): boolean => {
  if (Array.isArray(item.prerequisites) && item.prerequisites.length > 0) return true
  if (Array.isArray(item.dependencyNodeIds) && item.dependencyNodeIds.length > 0) return true
  if (Array.isArray(item.gateInputIds) && item.gateInputIds.length > 0) return true
  return false
}

// ---------- Materialization -----------------------------------------------

export const MAX_RECURRING_WINDOW_DAYS = 3

const DEFAULT_WINDOW_DAYS = DEFAULT_RECURRING_WINDOW_DAYS
const MAX_WINDOW_DAYS = MAX_RECURRING_WINDOW_DAYS

/** Compute the ordered list of occurrence keys within a workspace's rolling
 *  window: today + next (windowDays - 1) days (or weeks). Used by the
 *  details-popup upcoming preview and downstream helpers. */
export const rollingWindowKeys = (mode: RecurringMode, windowDays: number, now: Date = new Date(), timeZone?: string): string[] =>
  windowKeysFor(mode, windowDays, now, timeZone)

/** Build the empty-occurrence payload map for a rolling window — used
 *  when a client authors a new recurring item and wants to seed a local
 *  `occurrences` mirror so the item shows up on today + next N-1 pages
 *  before the first book-page snapshot lands. */
export const buildInitialRecurringOccurrences = (
  mode: RecurringMode,
  windowDays: number,
  now: Date = new Date(),
): Record<string, RecurringOccurrenceState> => {
  const keys = windowKeysFor(mode, windowDays, now)
  const map: Record<string, RecurringOccurrenceState> = {}
  for (const key of keys) {
    map[key] = {
      isCompleted: false,
      workflowStatus: 'CREATED',
      completedAt: null,
      completedById: null,
      completedByName: null,
    }
  }
  return map
}

const windowKeysFor = (mode: RecurringMode, windowDays: number, now: Date = new Date(), timeZone?: string): string[] => {
  const size = Math.min(MAX_WINDOW_DAYS, Math.max(1, Math.round(normalizeRecurringWindowDays(windowDays || DEFAULT_WINDOW_DAYS))))
  const keys: string[] = []
  if (mode === 'DAILY') {
    for (let i = 0; i < size; i += 1) {
      const d = new Date(now); d.setDate(d.getDate() + i)
      keys.push(isoDate(d, timeZone))
    }
  } else {
    const monday = startOfIsoWeek(now, timeZone)
    for (let i = 0; i < size; i += 1) {
      const d = new Date(monday); d.setDate(d.getDate() + i * 7)
      keys.push(isoWeekKey(d, timeZone))
    }
  }
  return keys
}

// ---------- Bootstrap ------------------------------------------------------

/** Per-workspace on-load bootstrap. In the book-model era only the settings
 *  doc needs to exist; every other legacy-era migrator wrote occurrence-map
 *  fields onto `checklists/{id}` and cascaded through
 *  `mirrorDefinitionCompletionToPage` with stale (empty) data — silently
 *  zeroing today's completions on every ChecklistsShell mount. Book-model
 *  window materialisation happens in `materializeRecurringBookLookahead`
 *  (called from ChecklistsShell). Retention prune happens per-book via
 *  `pruneOldPages`. Idempotent. */
export const bootstrapRecurringForWorkspace = async (
  workspaceId: string,
  isSubscriber: boolean,
): Promise<void> => {
  if (!workspaceId) return
  await ensureWorkspaceRecurringSettings(workspaceId, isSubscriber)
}

/** Path helper — used by ChecklistDrawingBoard's ad-hoc queries.
 *  Deliberately re-exported so callers don't have to encode
 *  `pageKeyFor(type, date)` themselves. */
export { pageKeyFor }
